
/**
 * Fetch static page data from CMS API
 * @param {string} pageSlug - The page slug ('terms-conditions' or 'privacy-policy')
 * @param {string} lang - Language code (en, ar, urdu, french, korean, filipino, arabic, persian, pashto, hindi)
 * @returns {Promise} - Promise that resolves to the page data
 */
export const fetchStaticPageData = async (pageSlug, lang = 'en') => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/static-page/static-page-data?page_slug=${pageSlug}&lang=${lang}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching static page data:', error);
        throw error;
    }
};

/**
 * Map language codes to match the API format
 */
export const SUPPORTED_LANGUAGES = [
    'en',
    'ar',
    'urdu',
    'french',
    'korean',
    'filipino',
    'arabic',
    'persian',
    'pashto',
    'hindi'
];

/**
 * Map frontend language codes to API language codes
 * This ensures the selected language from dropdown maps correctly to API
 */
export const mapLanguageCode = (frontendLang) => {
    const languageMap = {
        // Current frontend language codes (matching LanguageContext)
        'en': 'en',
        'hi': 'hindi',
        'ar': 'arabic',           // Arabic from dropdown
        'ur': 'urdu',            // Urdu from dropdown
        'fr': 'french',          // French from dropdown
        'ko': 'korean',          // Korean from dropdown
        'filipino': 'filipino',  // Filipino from dropdown
        'persian': 'persian',    // Persian from dropdown
        'pashto': 'pashto',      // Pashto from dropdown

        // Alternative/standard language codes
        'french': 'french',      // Legacy support
        'korean': 'korean',      // Legacy support
        'tl': 'filipino',
        'fil': 'filipino', // Alternative Filipino code
        'fa': 'persian',
        'ps': 'pashto',

        // Direct mappings (if API uses same codes)
        'arabic': 'arabic',
        'urdu': 'urdu',
        'hindi': 'hindi'
    };

    const mappedLang = languageMap[frontendLang?.toLowerCase()];

    // Log for debugging
    console.log(`Language mapping: ${frontendLang} -> ${mappedLang || frontendLang || 'en'}`);

    return mappedLang || frontendLang || 'en';
};

/**
 * Page slug constants
 */
export const PAGE_SLUGS = {
    TERMS: 'terms-conditions',
    PRIVACY: 'privacy-policy'
};

/**
 * Save a comment to the backend
 * @param {Object} commentData - The comment data to save
 * @param {string} commentData.name - User's name
 * @param {string} commentData.job - User's job title
 * @param {string} commentData.comment - User's comment
 * @param {string} lang - Language code
 * @returns {Promise} - Promise that resolves to the saved comment data
 */
export const saveComment = async (commentData, lang = 'en') => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/comment/add`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...commentData,
                    lang: mapLanguageCode(lang),
                    timestamp: new Date().toISOString()
                }),
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error saving comment:', error);
        throw error;
    }
};

/**
 * Fetch active comments from the backend
 * @param {string} lang - Language code
 * @param {number} limit - Number of comments to fetch (default: 10)
 * @param {number} page - Page number for pagination (default: 1)
 * @returns {Promise} - Promise that resolves to the comments data
 */
export const fetchComments = async (lang = 'en', limit = 10, page = 1) => {
    try {
        const params = new URLSearchParams({
            lang: mapLanguageCode(lang),
            limit: limit.toString(),
            page: page.toString()
        });

        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/comment/active?${params}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching comments:', error);
        throw error;
    }
};

/**
 * Test function to verify API integration
 * You can call this in browser console to test
 */
export const testApiCall = async (pageSlug = 'privacy-policy', lang = 'en') => {
    try {
        console.log(`Testing API call for ${pageSlug} in ${lang}...`);
        const data = await fetchStaticPageData(pageSlug, lang);
        console.log('API Response:', data);
        return data;
    } catch (error) {
        console.error('API Test Failed:', error);
        return null;
    }
};
